// frontend/src/pages/admin/Users.js
import React, { useState, useEffect } from 'react';
import axios from '../../utils/axios';
import { toast } from 'react-toastify';
import {
  FiUsers,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiSearch,
  FiShield,
  FiCheckSquare,
  FiSquare,
  FiKey,
  FiAlertCircle
} from 'react-icons/fi';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [pagination, setPagination] = useState({});

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedUser, setSelectedUser] = useState(null);

  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    password: '',
    full_name: '',
    address: '',
    role_id: '',
    is_active: true
  });

  const [permissions, setPermissions] = useState({});
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    fetchCurrentUser();
    fetchUsers();
    fetchRoles();
  }, [searchQuery, selectedRole]);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get('/api/auth/me');
      if (response.data.success) {
        setCurrentUser(response.data.data.user);
      }
    } catch (error) {
      console.error('Fetch current user error:', error);
    }
  };

  const fetchUsers = async (page = 1) => {
    setLoading(true);
    try {
      const response = await axios.get('/api/users', {
        params: {
          page,
          limit: 20,
          search: searchQuery,
          role_id: selectedRole,
          exclude_customers: 'true'  // ✅ ХАРИЛЦАГЧДЫГ ХАСАХ
        }
      });

      if (response.data.success) {
        setUsers(response.data.data.users);
        setPagination(response.data.data.pagination);
      }
    } catch (error) {
      console.error('Fetch users error:', error);
      toast.error('Хэрэглэгчийн жагсаалт татахад алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axios.get('/api/auth/roles');
      if (response.data.success) {
        // ✅ Customer роль ХАСАХ
        const filteredRoles = response.data.data.roles.filter(
          role => role.name !== 'customer'
        );
        setRoles(filteredRoles);
      }
    } catch (error) {
      console.error('Fetch roles error:', error);
    }
  };

  const canEditUser = (user) => {
  if (!currentUser) return false;
  
  // ✅ Өөрийгөө засах боломжгүй
  if (user.id === currentUser.id) return false;
  
  // ✅ Admin бусад Admin-ыг засна
  if (currentUser.role_name === 'admin') {
    return true;
  }
  
  // ✅ Staff Admin-ыг засах боломжгүй
  if (user.role_name === 'admin') return false;
  
  // ✅ Staff бусад Staff-ыг засна (хэрэв Staff-д эрх өгөхийг хүсвэл)
  return true;
};

  const canDeleteUser = (user) => {
  if (!currentUser) return false;
  
  // ✅ Өөрийгөө устгах боломжгүй
  if (user.id === currentUser.id) return false;
  
  // ✅ Admin бусад Admin-ыг устгана
  if (currentUser.role_name === 'admin') {
    return true;
  }
  
  // ✅ Staff Admin-ыг устгах боломжгүй
  if (user.role_name === 'admin') return false;
  
  // ✅ Staff бусад Staff-ыг устгана
  return true;
};

  const canManagePermissions = (user) => {
  if (!currentUser) return false;
  
  // ✅ Өөрийгөө засах боломжгүй
  if (user.id === currentUser.id) return false;
  
  // ✅ Admin бусад Admin-ын эрхийг засна
  if (currentUser.role_name === 'admin') {
    return true;
  }
  
  // ✅ Staff Admin-ын эрхийг засах боломжгүй
  if (user.role_name === 'admin') return false;
  
  // ✅ Staff бусад Staff-ын эрхийг засна
  return true;
};

  const openCreateModal = () => {
    setModalMode('create');
    setFormData({
      email: '',
      phone: '',
      password: '',
      full_name: '',
      address: '',
      role_id: '',
      is_active: true
    });
    setShowModal(true);
  };

  const openEditModal = (user) => {
    if (!canEditUser(user)) {
      toast.error('Энэ хэрэглэгчийг засах эрхгүй байна');
      return;
    }
    
    setModalMode('edit');
    setSelectedUser(user);
    setFormData({
      email: user.email,
      phone: user.phone || '',
      password: '',
      full_name: user.full_name,
      address: user.address || '',
      role_id: user.role_id,
      is_active: user.is_active
    });
    setShowModal(true);
  };

  const openPermissionsModal = async (user) => {
    if (!canManagePermissions(user)) {
      toast.error('Энэ хэрэглэгчийн эрхийг засах эрхгүй байна');
      return;
    }
    
    setModalMode('permissions');
    setSelectedUser(user);
    setLoading(true);
    
    try {
      const response = await axios.get(`/api/users/${user.id}/permissions`);
      if (response.data.success) {
        // ✅ ЗӨВХӨН ШААРДЛАГАТАЙ ЭРХҮҮД
        const filteredPermissions = {};
        Object.entries(response.data.data.permissions).forEach(([category, perms]) => {
          if (category === 'products') {
            filteredPermissions[category] = perms.filter(p => 
              ['view_products', 'create_products', 'edit_products', 'delete_products'].includes(p.code)
            );
          } else if (['inventory', 'sales', 'orders', 'reports', 'users'].includes(category)) {
            filteredPermissions[category] = perms;
          }
        });
        
        setPermissions(filteredPermissions);
        
        const granted = [];
        Object.values(filteredPermissions).forEach(category => {
          category.forEach(perm => {
            if (perm.granted) {
              granted.push(perm.id);
            }
          });
        });
        setSelectedPermissions(granted);
      }
    } catch (error) {
      console.error('Fetch permissions error:', error);
      toast.error('Эрхүүдийн жагсаалт татахад алдаа гарлаа');
    } finally {
      setLoading(false);
    }
    
    setShowModal(true);
  };

  const openPasswordModal = (user) => {
    if (!canEditUser(user)) {
      toast.error('Энэ хэрэглэгчийн нууц үг солих эрхгүй байна');
      return;
    }
    
    setModalMode('password');
    setSelectedUser(user);
    setNewPassword('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (modalMode === 'create') {
        const response = await axios.post('/api/users', formData);
        if (response.data.success) {
          toast.success('Хэрэглэгч амжилттай бүртгэгдлээ');
          fetchUsers();
          setShowModal(false);
        }
      } else if (modalMode === 'edit') {
        const response = await axios.put(`/api/users/${selectedUser.id}`, formData);
        if (response.data.success) {
          toast.success('Мэдээлэл шинэчлэгдлээ');
          fetchUsers();
          setShowModal(false);
        }
      } else if (modalMode === 'permissions') {
        const response = await axios.put(`/api/users/${selectedUser.id}/permissions`, {
          permissions: selectedPermissions
        });
        if (response.data.success) {
          toast.success('Эрхүүд шинэчлэгдлээ');
          setShowModal(false);
        }
      } else if (modalMode === 'password') {
        const response = await axios.post(`/api/users/${selectedUser.id}/reset-password`, {
          new_password: newPassword
        });
        if (response.data.success) {
          toast.success('Нууц үг амжилттай солигдлоо');
          setShowModal(false);
        }
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.response?.data?.message || 'Алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const user = users.find(u => u.id === id);
    if (!canDeleteUser(user)) {
      toast.error('Энэ хэрэглэгчийг устгах эрхгүй байна');
      return;
    }
    
    if (!window.confirm('⚠️ Хэрэглэгчийг бүрмөсөн устгах уу?')) return;

    setLoading(true);

    try {
      const response = await axios.delete(`/api/users/${id}`);
      if (response.data.success) {
        toast.success('Хэрэглэгч устгагдлаа');
        setUsers(prevUsers => prevUsers.filter(user => user.id !== id));
        setPagination(prev => ({
          ...prev,
          total: prev.total - 1
        }));
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.message || 'Устгахад алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  const togglePermission = (permId) => {
    if (selectedPermissions.includes(permId)) {
      setSelectedPermissions(selectedPermissions.filter(id => id !== permId));
    } else {
      setSelectedPermissions([...selectedPermissions, permId]);
    }
  };

  const toggleCategoryPermissions = (category) => {
    const categoryPerms = permissions[category] || [];
    const categoryPermIds = categoryPerms.map(p => p.id);
    
    const allSelected = categoryPermIds.every(id => selectedPermissions.includes(id));
    
    if (allSelected) {
      setSelectedPermissions(selectedPermissions.filter(id => !categoryPermIds.includes(id)));
    } else {
      setSelectedPermissions([...new Set([...selectedPermissions, ...categoryPermIds])]);
    }
  };

  const toggleAllPermissions = () => {
    const allPermIds = [];
    Object.values(permissions).forEach(category => {
      category.forEach(perm => allPermIds.push(perm.id));
    });

    if (selectedPermissions.length === allPermIds.length) {
      setSelectedPermissions([]);
    } else {
      setSelectedPermissions(allPermIds);
    }
  };

  const categoryLabels = {
    products: '📦 Бараа модуль',
    inventory: '🏢 Агуулах модуль',
    sales: '💰 Борлуулалт модуль',
    orders: '📋 Захиалга модуль',
    reports: '📊 Тайлан модуль',
    users: '🔐 Хэрэглэгч модуль'
  };

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <FiUsers className="mr-2" />
            Хэрэглэгчийн удирдлага
          </h1>
          <p className="text-gray-600 mt-1 text-sm">
            Admin, Staff хэрэглэгч бүртгэх, эрх олгох
          </p>
        </div>
        <button onClick={openCreateModal} className="btn btn-primary whitespace-nowrap">
          <FiPlus className="mr-2" />
          Шинэ хэрэглэгч
        </button>
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FiSearch className="text-gray-400" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Нэр, и-мэйл, утасаар хайх..."
                className="input pl-10"
              />
            </div>
          </div>

          <div>
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="input"
            >
              <option value="">Бүх эрх</option>
              {roles.map(role => (
                <option key={role.id} value={role.id}>{role.display_name || role.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
          <table className="table w-full">
            <thead className="table-header sticky top-0 bg-gray-50 z-10">
              <tr>
                <th className="table-th">ID</th>
                <th className="table-th">Хэрэглэгч</th>
                <th className="table-th">Холбоо барих</th>
                <th className="table-th">Эрх</th>
                <th className="table-th">Төлөв</th>
                <th className="table-th">Бүртгэсэн</th>
                <th className="table-th text-right">Үйлдэл</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="text-center py-12">
                    <div className="spinner-lg mx-auto mb-3"></div>
                    <p className="text-gray-500">Уншиж байна...</p>
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-12 text-gray-500">
                    <FiUsers size={48} className="mx-auto mb-3 opacity-50" />
                    <p>Хэрэглэгч олдсонгүй</p>
                  </td>
                </tr>
              ) : (
                users.map(user => (
                  <tr key={user.id} className="table-row">
                    <td className="table-td">
                      <span className="font-mono text-sm text-gray-600">#{user.id}</span>
                    </td>
                    <td className="table-td">
                      <div className="flex items-center">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 flex-shrink-0 shadow-md ${
                          currentUser && user.id === currentUser.id
                            ? 'bg-gradient-to-br from-purple-500 to-pink-500'
                            : 'bg-gradient-to-br from-blue-500 to-blue-600'
                        }`}>
                          <span className="text-white font-bold text-lg">
                            {user.full_name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-gray-900 truncate">{user.full_name}</p>
                            {currentUser && user.id === currentUser.id && (
                              <span className="badge badge-purple text-xs">Та</span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 truncate">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="table-td">
                      <p className="text-sm text-gray-600">{user.phone || '-'}</p>
                    </td>
                    <td className="table-td">
                      <span className={`badge ${
                        user.role_name === 'admin' ? 'badge-red' :
                        user.role_name === 'staff' ? 'badge-blue' :
                        'badge-green'
                      }`}>
                        {user.role_name === 'admin' ? '👑 Admin' : 
                         user.role_name === 'staff' ? '👔 Staff' : user.role_name}
                      </span>
                    </td>
                    <td className="table-td">
                      {user.is_active ? (
                        <span className="badge badge-success">✅ Идэвхтэй</span>
                      ) : (
                        <span className="badge badge-secondary">❌ Идэвхгүй</span>
                      )}
                    </td>
                    <td className="table-td text-sm text-gray-600">
                      {new Date(user.created_at).toLocaleDateString('mn-MN')}
                    </td>
                    <td className="table-td text-right">
                      <div className="flex justify-end space-x-2">
                        {canManagePermissions(user) && (
                          <button
                            onClick={() => openPermissionsModal(user)}
                            className="btn-icon btn-icon-blue"
                            title="Эрх удирдах"
                          >
                            <FiShield size={16} />
                          </button>
                        )}
                        {canEditUser(user) && (
                          <>
                            <button
                              onClick={() => openPasswordModal(user)}
                              className="btn-icon btn-icon-purple"
                              title="Нууц үг солих"
                            >
                              <FiKey size={16} />
                            </button>
                            <button
                              onClick={() => openEditModal(user)}
                              className="btn-icon btn-icon-yellow"
                              title="Засах"
                            >
                              <FiEdit2 size={16} />
                            </button>
                          </>
                        )}
                        {canDeleteUser(user) && (
                          <button
                            onClick={() => handleDelete(user.id)}
                            className="btn-icon btn-icon-red"
                            title="Устгах"
                          >
                            <FiTrash2 size={16} />
                          </button>
                        )}
                        {!canEditUser(user) && !canDeleteUser(user) && !canManagePermissions(user) && (
                          <span className="text-xs text-gray-400 italic">Эрхгүй</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination.pages > 1 && (
          <div className="flex justify-center mt-6 px-4 pb-4">
            <div className="flex flex-wrap gap-2 justify-center">
              {[...Array(pagination.pages)].map((_, i) => (
                <button
                  key={i}
                  onClick={() => fetchUsers(i + 1)}
                  className={`px-4 py-2 rounded-lg transition font-medium ${
                    pagination.page === i + 1
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {i + 1}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
              <h3 className="text-xl font-bold text-gray-900 flex items-center">
                {modalMode === 'create' && '✨ Шинэ хэрэглэгч бүртгэх'}
                {modalMode === 'edit' && '✏️ Хэрэглэгчийн мэдээлэл засах'}
                {modalMode === 'permissions' && '🔐 Эрх удирдах'}
                {modalMode === 'password' && '🔑 Нууц үг солих'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-200 transition"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
              <div className="flex-1 overflow-y-auto p-6">
                {modalMode === 'password' ? (
                  <div className="space-y-5">
                    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded flex items-start">
                      <FiAlertCircle className="text-yellow-600 mr-3 mt-0.5 flex-shrink-0" size={20} />
                      <p className="text-sm text-yellow-800">
                        <strong>⚠️ Анхаар:</strong> Нууц үг солигдсоны дараа хэрэглэгч шинэ нууц үгээр нэвтрэх ёстой.
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        🔒 Шинэ нууц үг <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="input"
                        placeholder="Хамгийн багадаа 6 тэмдэгт"
                        required
                        minLength={6}
                      />
                      <p className="text-xs text-gray-500 mt-1">Хамгийн багадаа 6 тэмдэгттэй байх ёстой</p>
                    </div>
                  </div>
                ) : modalMode !== 'permissions' ? (
                  <div className="space-y-5">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        👤 Овог нэр <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.full_name}
                        onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                        className="input"
                        placeholder="Жишээ: Б.Бат"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          ✉️ И-мэйл <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          className="input"
                          placeholder="email@example.com"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          📞 Утас
                        </label>
                        <input
                          type="text"
                          value={formData.phone}
                          onChange={(e) => setFormData({...formData, phone: e.target.value})}
                          className="input"
                          placeholder="99001122"
                        />
                      </div>
                    </div>

                    {modalMode === 'create' && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          🔒 Нууц үг <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="password"
                          value={formData.password}
                          onChange={(e) => setFormData({...formData, password: e.target.value})}
                          className="input"
                          placeholder="Хамгийн багадаа 6 тэмдэгт"
                          required
                          minLength={6}
                        />
                        <p className="text-xs text-gray-500 mt-1">Хамгийн багадаа 6 тэмдэгттэй байх ёстой</p>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        🎭 Эрх <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={formData.role_id}
                        onChange={(e) => setFormData({...formData, role_id: e.target.value})}
                        className="input"
                        required
                      >
                        <option value="">Эрх сонгох</option>
                        {roles.map(role => (
                          <option key={role.id} value={role.id}>
                            {role.display_name || role.name}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        💡 Admin эсвэл Staff эрх сонгоно
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        📍 Хаяг
                      </label>
                      <textarea
                        value={formData.address}
                        onChange={(e) => setFormData({...formData, address: e.target.value})}
                        className="input"
                        rows="3"
                        placeholder="Улаанбаатар хот, СБД, 1-р хороо..."
                      ></textarea>
                    </div>

                    <div className="flex items-center p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                      <input
                        type="checkbox"
                        id="is_active"
                        checked={formData.is_active}
                        onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                        className="w-5 h-5 text-green-600 focus:ring-2 focus:ring-green-500 rounded"
                      />
                      <label htmlFor="is_active" className="ml-3 text-sm font-medium text-gray-700">
                        ✅ Идэвхтэй хэрэглэгч
                      </label>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                      <span className="font-semibold text-blue-900">Бүх эрх сонгох</span>
                      <button
                        type="button"
                        onClick={toggleAllPermissions}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center space-x-2 shadow-md"
                      >
                        {selectedPermissions.length === Object.values(permissions).flat().length ? (
                          <>
                            <FiSquare size={18} />
                            <span>Бүгдийг хасах</span>
                          </>
                        ) : (
                          <>
                            <FiCheckSquare size={18} />
                            <span>Бүгдийг сонгох</span>
                          </>
                        )}
                      </button>
                    </div>

                    {Object.entries(permissions).map(([category, perms]) => {
                      const categoryPermIds = perms.map(p => p.id);
                      const allCategorySelected = categoryPermIds.every(id => selectedPermissions.includes(id));
                      
                      return (
                        <div key={category} className="border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition">
                          <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 border-b flex items-center justify-between">
                            <h4 className="font-bold text-gray-900 text-lg">
                              {categoryLabels[category] || category}
                            </h4>
                            <button
                              type="button"
                              onClick={() => toggleCategoryPermissions(category)}
                              className="text-sm px-3 py-1.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition shadow-sm font-medium"
                            >
                              {allCategorySelected ? '❌ Бүгдийг хасах' : '✅ Бүгдийг сонгох'}
                            </button>
                          </div>

                          <div className="p-3 space-y-2">
                            {perms.map(perm => (
                              <label 
                                key={perm.id} 
                                className="flex items-start cursor-pointer hover:bg-blue-50 p-3 rounded-lg transition border border-transparent hover:border-blue-200"
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedPermissions.includes(perm.id)}
                                  onChange={() => togglePermission(perm.id)}
                                  className="mt-1 mr-3 w-5 h-5 text-blue-600 focus:ring-2 focus:ring-blue-500 rounded"
                                />
                                <div className="flex-1">
                                  <p className="font-semibold text-gray-900">{perm.name}</p>
                                  <p className="text-sm text-gray-500 mt-0.5">{perm.description}</p>
                                </div>
                              </label>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 p-6 border-t bg-gray-50">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2.5 bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium"
                >
                  Болих
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                >
                  {loading ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Хадгалж байна...
                    </span>
                  ) : (
                    '💾 Хадгалах'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;